/**
 * User Preferences Module
 * Manages immediate UI preference changes on the current page.
 * Persisted source of truth for authenticated users lives on the server.
 */

import { clearJwtToken } from './jwtTokenManager.js';

const LEGACY_LAYOUT_COOKIE_NAME = 'layout_pref';
const LEGACY_LAYOUT_COOKIE_USER_NAME = 'layout_pref_user';
const LEGACY_THEME_COOKIE_NAME = 'theme_pref';
const LEGACY_THEME_COOKIE_USER_NAME = 'theme_pref_user';

/**
 * Derive cookie domain to match PHP Cookie::getCookieDomain().
 * Extracts the registrable domain (last 2 segments) so JS-set cookies
 * share the same scope as server-set cookies.
 * @returns {string} Domain string for cookie (e.g. ".interpals.net") or empty for localhost
 */
function getCookieDomain() {
  const parts = window.location.hostname.split('.');
  if (parts.length <= 2) return '';
  return '.' + parts.slice(-2).join('.');
}

/**
 * Set a cookie with the given name and value.
 * Used only for legacy preference-cookie cleanup during rollout.
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {number} days - Expiration in days (default: 365)
 */
function setCookie(name, value, days = 365) {
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  const domain = getCookieDomain();
  const domainStr = domain ? `domain=${domain}; ` : '';
  // Expire any stale host-only cookie (no domain) that would shadow the domain cookie
  if (domain) {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  }
  // No Secure flag — matches PHP Cookie::set() which uses secure=false
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; ${domainStr}SameSite=Lax`;
}

/**
 * Preserve the existing API shape. Layout now persists via server-side device overrides.
 * @param {string} layout - 'sidebar' or 'ultra-legacy'
 */
export function setLayoutPreference(layout) {
  window.currentLayout = layout;
}

function isValidLayout(layout) {
  return layout === 'sidebar' || layout === 'ultra-legacy';
}

function buildLayoutPreferenceUrl(layout, targetUrl = window.location.href) {
  const url = new URL(targetUrl, window.location.origin);
  url.searchParams.set('layout', layout);
  return url.toString();
}

/**
 * Switch layout by updating the cookie first, then navigating with ?layout=
 * so the server can persist the choice during the next page render.
 * @param {string} layout - 'sidebar' or 'ultra-legacy'
 * @param {string} targetUrl - Optional absolute or relative URL to navigate to
 */
export function switchLayoutPreference(layout, targetUrl = window.location.href) {
  if (!isValidLayout(layout)) return;
  setLayoutPreference(layout);
  window.location.assign(buildLayoutPreferenceUrl(layout, targetUrl));
}

/**
 * Persist layout preference to the server for the current web device
 * @param {string} layout - 'sidebar' or 'ultra-legacy'
 */
export function saveLayoutPreferenceToServer(layout) {
  if (!isValidLayout(layout)) {
    return Promise.resolve(false);
  }
  const formData = new FormData();
  formData.append('layout', layout);
  const token = document.querySelector('meta[name="csrf_token"]');
  if (token) formData.append('csrf_token', token.getAttribute('content'));
  return fetch('/app/save-layout-preference', {
    method: 'POST',
    body: formData,
    credentials: 'same-origin',
  })
    .then((response) => response.ok)
    .catch(() => false);
}

// ─── Theme preference ───

/**
 * Set theme preference cookie
 * @param {string} theme - 'light' or 'dark'
 */
export function setThemePreference(theme) {
  window.currentThemePreference = theme;
}

/**
 * Apply theme by toggling dark class on <html>
 * @param {string} theme - 'light' or 'dark'
 */
export function applyTheme(theme) {
  const layout = window.currentLayout || null;
  const shouldUseDark = theme === 'dark'
    || (theme === 'system'
      && window.matchMedia
      && window.matchMedia('(prefers-color-scheme: dark)').matches);

  if (shouldUseDark && layout !== 'ultra-legacy') {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
}

/**
 * Toggle between light and dark theme
 * Persists to cookie and server
 * @returns {string} The new theme ('light' or 'dark')
 */
export function toggleTheme() {
  const current = document.documentElement.classList.contains('dark') ? 'dark' : 'light';
  const next = current === 'dark' ? 'light' : 'dark';
  setThemePreference(next);
  applyTheme(next);
  saveThemePreferenceToServer(next);
  return next;
}

/**
 * Persist theme preference to the server for the current web device
 * @param {string} theme - 'light' or 'dark'
 */
export function saveThemePreferenceToServer(theme) {
  const formData = new FormData();
  formData.append('theme', theme);
  const token = document.querySelector('meta[name="csrf_token"]');
  if (token) formData.append('csrf_token', token.getAttribute('content'));
  fetch('/app/save-theme-preference', {
    method: 'POST',
    body: formData,
    credentials: 'same-origin',
  }).catch(() => {});
}

/**
 * Clear user-specific preference cookies on logout.
 * Each user gets their own preferences from DB on next login.
 */
export function clearAuthData() {
  const domain = getCookieDomain();
  const names = [
    LEGACY_THEME_COOKIE_NAME,
    LEGACY_THEME_COOKIE_USER_NAME,
    LEGACY_LAYOUT_COOKIE_NAME,
    LEGACY_LAYOUT_COOKIE_USER_NAME,
  ];
  names.forEach(function(name) {
    if (domain) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain};`;
    }
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  });
  // Logout must clear both auth state and legacy preference state from redux-state.
  clearJwtToken();
}

// Expose functions globally
if (typeof window !== 'undefined') {
  window.setLayoutPreference = setLayoutPreference;
  window.switchLayoutPreference = switchLayoutPreference;
  window.saveLayoutPreferenceToServer = saveLayoutPreferenceToServer;
  window.clearAuthData = clearAuthData;
  window.setThemePreference = setThemePreference;
  window.applyTheme = applyTheme;
  window.toggleTheme = toggleTheme;
  window.saveThemePreferenceToServer = saveThemePreferenceToServer;
}
